import React from "react";
import { Copy, Eye, EyeOff } from "lucide-react";
import businessSuiteIcon from "../assets/images/businessSuite/account.svg";
import businessVerifiedIcon from "../assets/images/businessSuite/verified.svg";
import type { BusinessProfileSummary } from "../services/businessProfileService";

export interface BusinessSuiteHeroAsset {
  id?: string;
  asset?: string;
  assetName?: string;
  image?: string;
}

interface BusinessSuiteHeroProps {
  isAssetFocusView: boolean;
  focusedWalletAsset: BusinessSuiteHeroAsset | null;
  assetFocusPrimaryLine: string;
  formattedPortfolioValue: string;
  showBalances: boolean | undefined;
  onCopyAssetHero: () => void;
  onToggleBalanceVisibility: () => void;
  renderQuoteCurrencyDropdown: (
    tone: "businessHero" | "assetFocus"
  ) => React.ReactNode;
  activeBusiness: BusinessProfileSummary | null;
  businessContextSwitcher?: React.ReactNode;
}

export default function BusinessSuiteHero({
  isAssetFocusView,
  focusedWalletAsset,
  assetFocusPrimaryLine,
  formattedPortfolioValue,
  showBalances,
  onCopyAssetHero,
  onToggleBalanceVisibility,
  renderQuoteCurrencyDropdown,
  activeBusiness,
  businessContextSwitcher,
}: BusinessSuiteHeroProps) {
  return (
    <div
      className={`md:min-h-[441px] min-h-[371px] md:px-[27px] md:py-[34px] px-[20px] py-[30px] rounded-lg w-full flex flex-col old-user-bg-trading-dashboard-sm`}
    >
      <div
        className={`flex items-start justify-between flex-col text-white flex-1 ${
          isAssetFocusView ? "relative z-10" : ""
        }`}
      >
        {isAssetFocusView && focusedWalletAsset ? (
          <>
            <div className="flex items-center gap-3 w-full">
              <img
                src={focusedWalletAsset.image}
                alt=""
                className="w-[52px] h-[52px] rounded-full object-cover bg-white/10"
              />
              <span className="text-[18px] font-semibold tracking-tight">
                {focusedWalletAsset.assetName ||
                  focusedWalletAsset.asset?.toUpperCase()}
              </span>
            </div>
            <div className="flex flex-col flex-1 w-full justify-end mt-8">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between w-full">
                <div className="flex flex-col gap-2 min-w-0 flex-1">
                  <div className="text-white font-semibold text-[34px] md:text-[52px] leading-[1.1] break-words">
                    {assetFocusPrimaryLine}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 self-start sm:self-end">
                  <button
                    type="button"
                    onClick={onCopyAssetHero}
                    className="w-[48px] h-[48px] flex items-center justify-center rounded-full border border-white/30 hover:bg-white/10 transition-colors"
                    aria-label="Copy balance"
                  >
                    <Copy className="w-[20px] h-[20px]" />
                  </button>
                  <button
                    type="button"
                    onClick={onToggleBalanceVisibility}
                    className="w-[48px] h-[48px] flex items-center justify-center rounded-full border border-white/30 hover:bg-white/10 transition-colors"
                    aria-label={
                      showBalances === false ? "Show balance" : "Hide balance"
                    }
                  >
                    {showBalances === false ? (
                      <EyeOff className="w-[22px]" />
                    ) : (
                      <Eye className="w-[22px]" />
                    )}
                  </button>
                  {renderQuoteCurrencyDropdown("assetFocus")}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start gap-4 w-full">
              {businessContextSwitcher ? (
                businessContextSwitcher
              ) : (
                <div className="flex items-center gap-2 min-w-0">
                  <img
                    src={activeBusiness?.logoUrl || businessSuiteIcon}
                    alt={activeBusiness?.businessName || "Business Suite"}
                    className="w-[60px] h-[60px] rounded-full object-cover bg-white"
                  />
                  <div className="min-w-0">
                    <p className="text-[16px] text-[#EBE9EF] flex items-center gap-2 min-w-0">
                      <span className="truncate">
                        {activeBusiness?.businessName || "Business Suite"}
                      </span>
                      {activeBusiness ? (
                        <img
                          src={businessVerifiedIcon}
                          alt="Verified"
                          className="w-[20px] h-[20px] shrink-0"
                        />
                      ) : null}
                    </p>
                    <p className="text-white text-[24px] font-semibold">
                      {activeBusiness?.role?.name || "Business account"}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col w-full">
              <div className="flex items-center justify-between space-x-2 w-full">
                <div
                  className={`text-[#EBE9EF] md:text-[60px] text-[40px] font-semibold break-words whitespace-normal min-w-0 flex-1 overflow-hidden`}
                >
                  {formattedPortfolioValue}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onToggleBalanceVisibility}
                    className="w-[48px] h-[48px] flex items-center justify-center rounded-full border border-secondary/50 hover:bg-background/30 hover:text-white transition-colors"
                    aria-label={
                      showBalances === false ? "Show balance" : "Hide balance"
                    }
                  >
                    {showBalances === false ? (
                      <EyeOff className="w-[22px]" />
                    ) : (
                      <Eye className="w-[22px]" />
                    )}
                  </button>
                  {renderQuoteCurrencyDropdown("businessHero")}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
